import DocsLayout, { FeatureCard, Callout } from '../../components/docs/DocsLayout';
import { Coins, Users, BarChart3, Lock } from 'lucide-react';

export default function DocsTokenomics() {
  return (
    <DocsLayout 
      title="Tokenomics" 
      description="Token economics and distribution"
    >
      <section className="space-y-6">
        <p className="text-white/70 leading-relaxed">
          The AIBL token powers the protocol's incentive mechanisms, governance, and fee sharing. This page outlines the token distribution, utility, and economic model.
        </p>

        <Callout type="info" title="Token Launch">
          The AIBL token is planned for launch in Q2 2025. Details may change based on community feedback and market conditions.
        </Callout>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Token Utility</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose">
          <FeatureCard
            icon={<Coins size={24} className="text-yellow-400" />}
            title="Fee Discounts"
            description="Token holders receive reduced protocol fees based on their staking tier."
          />
          <FeatureCard
            icon={<Users size={24} className="text-blue-400" />}
            title="Governance"
            description="Vote on protocol upgrades, fee structures, and treasury allocation."
          />
          <FeatureCard
            icon={<BarChart3 size={24} className="text-green-400" />}
            title="Revenue Sharing"
            description="Stakers earn a share of protocol fees proportional to their stake."
          />
          <FeatureCard
            icon={<Lock size={24} className="text-purple-400" />}
            title="Priority Access"
            description="Early access to new features, higher rate limits, and premium support."
          />
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Token Distribution</h2>
        
        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <div className="w-32 text-sm text-white/70">Community</div>
            <div className="flex-1 bg-white/5 rounded-full h-4 overflow-hidden">
              <div className="bg-green-500 h-full" style={{ width: '40%' }} />
            </div>
            <div className="w-12 text-sm text-white/70 text-right">40%</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-32 text-sm text-white/70">Team</div>
            <div className="flex-1 bg-white/5 rounded-full h-4 overflow-hidden">
              <div className="bg-blue-500 h-full" style={{ width: '20%' }} />
            </div>
            <div className="w-12 text-sm text-white/70 text-right">20%</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-32 text-sm text-white/70">Investors</div>
            <div className="flex-1 bg-white/5 rounded-full h-4 overflow-hidden">
              <div className="bg-purple-500 h-full" style={{ width: '15%' }} />
            </div>
            <div className="w-12 text-sm text-white/70 text-right">15%</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-32 text-sm text-white/70">Treasury</div>
            <div className="flex-1 bg-white/5 rounded-full h-4 overflow-hidden">
              <div className="bg-yellow-500 h-full" style={{ width: '15%' }} />
            </div>
            <div className="w-12 text-sm text-white/70 text-right">15%</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-32 text-sm text-white/70">Liquidity</div>
            <div className="flex-1 bg-white/5 rounded-full h-4 overflow-hidden">
              <div className="bg-orange-500 h-full" style={{ width: '10%' }} />
            </div>
            <div className="w-12 text-sm text-white/70 text-right">10%</div>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Vesting Schedule</h2>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-white/50 font-medium">Allocation</th>
                <th className="text-left py-3 px-4 text-white/50 font-medium">Cliff</th>
                <th className="text-left py-3 px-4 text-white/50 font-medium">Vesting</th>
                <th className="text-left py-3 px-4 text-white/50 font-medium">TGE Unlock</th>
              </tr>
            </thead>
            <tbody className="text-white/70">
              <tr className="border-b border-white/5">
                <td className="py-3 px-4">Community</td>
                <td className="py-3 px-4">None</td>
                <td className="py-3 px-4">24 months</td>
                <td className="py-3 px-4">10%</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-4">Team</td>
                <td className="py-3 px-4">12 months</td>
                <td className="py-3 px-4">36 months</td>
                <td className="py-3 px-4">0%</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-4">Investors</td>
                <td className="py-3 px-4">6 months</td>
                <td className="py-3 px-4">24 months</td>
                <td className="py-3 px-4">0%</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-4">Treasury</td>
                <td className="py-3 px-4">None</td>
                <td className="py-3 px-4">DAO controlled</td>
                <td className="py-3 px-4">5%</td>
              </tr>
              <tr>
                <td className="py-3 px-4">Liquidity</td>
                <td className="py-3 px-4">None</td>
                <td className="py-3 px-4">None</td>
                <td className="py-3 px-4">100%</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Staking Tiers</h2>
        
        <div className="space-y-4">
          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-white">Bronze</h3>
              <span className="text-sm text-white/50">1,000+ AIBL</span>
            </div>
            <p className="text-sm text-white/60">5% fee discount, basic governance rights</p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-white">Silver</h3>
              <span className="text-sm text-white/50">10,000+ AIBL</span>
            </div>
            <p className="text-sm text-white/60">15% fee discount, revenue sharing, priority support</p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-white">Gold</h3>
              <span className="text-sm text-white/50">50,000+ AIBL</span>
            </div>
            <p className="text-sm text-white/60">25% fee discount, enhanced revenue sharing, early access</p>
          </div>

          <div className="p-5 rounded-xl bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-yellow-400">Platinum</h3>
              <span className="text-sm text-yellow-400/70">100,000+ AIBL</span>
            </div>
            <p className="text-sm text-white/60">50% fee discount, maximum revenue share, dedicated support</p>
          </div>
        </div>
      </section>
    </DocsLayout>
  );
}
